<template>
  <a-modal
    :visible="visible"
    :body-style="{ height: '710px' }"
    width="630px"
    :keyboard="false"
    centered
    :mask-closable="false"
    :title="isAutomatic ? null : 'Crea tu IVR tipo encuesta de satisfacción'"
    wrapClassName="create-ivr-modal"
    @cancel="handleCancel"
  >
    <section v-if="isAutomatic" class="steps">
      <h3 class="modal-name">Crear IVR operador automático</h3>
      <CustomStepper v-model:value="currentStep" :items="steps" readonly />
    </section>

    <section class="section">
      <keep-alive>
        <component
          :isAutomatic="isAutomatic"
          :key="steps[currentStep]"
          :is="stepsComponents[currentStep]"
          ref="stepRef"
        />
      </keep-alive>
    </section>

    <template v-slot:footer>
      <a-button @click="handlePrevious"> {{ currentStep === 0 ? "Cancelar" : "Volver" }} </a-button>
      <a-button
        type="primary"
        :disabled="isNextDisabled"
        :loading="isLoading"
        @click="handleNext"
        id="ivr-modal-primary"
      >
        {{ shouldCreate ? "Crear IVR" : "Continuar" }}
      </a-button>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, computed, createVNode } from "vue";
import { Modal, message } from "ant-design-vue";
import { QuestionCircleOutlined } from "@ant-design/icons-vue";

import { ResponseError } from "@/app/shared/utils/httpManager";
import { BotCreateBody } from "@/@types/ivr";
import { formatSchedulePicker } from "@/app/chatbots/utils/formatSchedulePicker";
import { botServices } from "@/app/ivr/bot/services/BotServices";
import { FormState } from "@/@types/ivr/forms";
import { BotType } from "../../utils/enums";

import CustomStepper from "@/app/shared/components/organisms/CustomStepper.vue";
import DetailsStep from "@/app/ivr/bot/components/steps/DetailsStep.vue";
import SchedulesStep from "@/app/ivr/bot/components/steps/SchedulesStep.vue";
import IdentifiersStep from "@/app/ivr/bot/components/steps/IdentifiersStep.vue";

const steps = ["Datos Generales", "Horarios", "Identificadores"] as const;
const stepsComponents = [DetailsStep, SchedulesStep, IdentifiersStep] as const;

const props = defineProps<{
  visible: boolean;
  isAutomatic: boolean;
}>();

const emit = defineEmits<{
  (e: "update:visible", visible: boolean): void;
  (e: "goToDetails", botId: number): void;
}>();

const currentStep = ref(0);
const currentComponent = stepsComponents[currentStep.value];
const stepRef = ref<InstanceType<typeof currentComponent> | undefined>(undefined);

const formState = ref<FormState>({});
const loading = ref(false);

const isNextDisabled = computed(() => {
  if (currentStep.value === 0) {
    if (stepRef.value?.formState && "name" in stepRef.value.formState) {
      return !stepRef.value?.formState?.name?.length;
    }
    return true;
  }

  return false;
});

const closeModal = () => emit("update:visible", false);

const handleCancel = () => {
  Modal.confirm({
    centered: true,
    icon: createVNode(QuestionCircleOutlined),
    title: "¿Deseas descartar los cambios realizados?",
    content: "Recuerda que si no guardas los cambios registrados, estos se perderán.",
    okText: "Sí, descartar cambios",
    cancelText: "No, seguir editando",
    onOk: () => closeModal(),
  });
};

const shouldCreate = computed(() => {
  if (props.isAutomatic) return currentStep.value === steps.length - 1;

  return true;
});

const handlePrevious = () => {
  if (currentStep.value === 0) handleCancel();
  else currentStep.value--;
};

const handleNext = async () => {
  formState.value = { ...formState.value, ...stepRef.value?.formState };
  if (currentStep.value === 1) {
    if (stepRef.value?.formState && "submitForm" in stepRef.value.formState) {
      const result = await stepRef.value?.formState?.submitForm!();

      let isValid: boolean;

      if (result) {
        isValid = result.length ? result.every((i) => i) : true;
        if (!isValid) return;
      } else {
        isValid = true;
      }
    }
  }

  if (shouldCreate.value) handleCreate();
  else currentStep.value++;
};

const isLoading = ref(false);

const handleCreate = async () => {
  loading.value = true;

  const channelsCustomers = (formState.value.channels_customers ?? []).filter(Boolean);

  try {
    isLoading.value = true;
    const schedule = formatSchedulePicker(formState.value.arrayRangePickers);

    // console.log({ ...formState.value, schedule });
    // const form = await formService.create();

    const newBotBody: BotCreateBody = {
      name: formState.value.name,
      description: formState.value.description,
      portfolio: formState.value.portfolio as number,
      type: props.isAutomatic ? BotType.Automatic : BotType.Survey,
      channels_customers: channelsCustomers,
      schedules: schedule,
    };

    const fileAudio = {
      out_of_schedule_file_audio: formState.value?.out_of_schedule_file_audio,
    };

    const bot = await botServices.create(newBotBody);
    await botServices.uploadOutOfServiceAudio(bot.id, fileAudio);

    message.success("Se ha creado el bot");
    closeModal();

    emit("goToDetails", bot.id);
  } catch (error) {
    console.error(error);
    const errorMessage =
      error instanceof ResponseError ? error.getErrorMessage() : "Ha ocurrido un error";

    message.error(errorMessage);
  } finally {
    isLoading.value = false;
  }

  loading.value = false;
};
</script>

<style scoped lang="sass">
.section
  margin: 20px 40px
</style>
